import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({
  title,
  description,
  keywords,
  url,
  image,
  type = 'website',
  article = null,
  service = null,
  canonical = null,
  noindex = false,
  nofollow = false,
  author = 'DevHopes Labs',
  publishedTime = null,
  modifiedTime = null,
  section = null,
  tags = [],
  lang = 'en-US',
  schemaType = 'WebPage'
}) => {
  const siteUrl = 'https://devhopeslabs.com';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullImage = image ? `${siteUrl}${image}` : `${siteUrl}/images/logo.png`;
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : fullUrl;
  
  // Robot directives
  const robotDirectives = [];
  if (noindex) robotDirectives.push('noindex');
  else robotDirectives.push('index');
  
  if (nofollow) robotDirectives.push('nofollow');
  else robotDirectives.push('follow');
  
  robotDirectives.push('max-snippet:-1', 'max-image-preview:large', 'max-video-preview:-1');
  
  const robotsContent = robotDirectives.join(', ');

  // Structured Data for WebPage
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': schemaType,
    name: title,
    description: description,
    url: fullUrl,
    image: fullImage,
    inLanguage: lang,
    isPartOf: {
      '@type': 'WebSite',
      name: 'DevHopes Labs',
      url: siteUrl
    },
    author: {
      '@type': 'Organization',
      name: author,
      url: siteUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'DevHopes Labs',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/images/logo.png`
      }
    },
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl
    }
  };

  // Service Schema (for service pages)
  const serviceSchema = service ? {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: 'DevHopes Labs',
      url: siteUrl
    },
    serviceType: service.type,
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Business'
    },
    areaServed: service.areaServed || ['Netherlands', 'Europe', 'Worldwide'],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Development Services',
      itemListElement: service.offers || []
    }
  } : null;

  // Article Schema (for blog/article pages)
  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    image: fullImage,
    author: {
      '@type': 'Person',
      name: article.author || 'DevHopes Labs Team',
      url: siteUrl
    },
    publisher: {
      '@type': 'Organization',
      name: 'DevHopes Labs',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/images/logo.png`
      }
    },
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl
    },
    articleSection: section,
    keywords: tags.join(', '),
    wordCount: article.wordCount,
    timeRequired: article.readingTime
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      <meta name="language" content={lang} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:alt" content={`${title} - DevHopes Labs`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="DevHopes Labs" />
      <meta property="og:locale" content={lang.replace('-', '_')} />
      
      {/* Article specific Open Graph */}
      {article && (
        <>
          <meta property="article:author" content={article.author || 'DevHopes Labs Team'} />
          <meta property="article:published_time" content={publishedTime} />
          <meta property="article:modified_time" content={modifiedTime || publishedTime} />
          {section && <meta property="article:section" content={section} />}
          {tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={`${title} - DevHopes Labs`} />
      <meta name="twitter:creator" content="@devhopeslabs" />
      <meta name="twitter:site" content="@devhopeslabs" />
      
      {/* Additional SEO Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#14b8a6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="DevHopes Labs" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="application-name" content="DevHopes Labs" />
      <meta name="msapplication-TileColor" content="#14b8a6" />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(webPageSchema)}
      </script>
      
      {/* Service Schema */}
      {serviceSchema && (
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      )}
      
      {/* Article Schema */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
      
      {/* Breadcrumb Schema */}
      {url && url !== '/' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: siteUrl
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: title,
                item: fullUrl
              }
            ]
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead; 